<template>
  <el-table
    :data="tableData"
    stripe
    ref="tableServices"
    style="width: 100%"
    :default-sort="{ prop: 'name', order: 'ascending' }"
    @selection-change="onSelectionChange"
  >
    <el-table-column type="selection" width="80"></el-table-column>
    <el-table-column
      sortable
      prop="servicegroup"
      :label="$t('grupo-servico')"
      :filters="filterGroup"
      :filter-method="filterHandler"
    >
    </el-table-column>
    <el-table-column
      sortable
      prop="code"
      width="120"
      :label="$t('codigo')"
    ></el-table-column>
    <el-table-column sortable prop="name" :label="$t('nome')"></el-table-column>
    <el-table-column sortable prop="postoffice" :label="`${$t('correio')}?`">
      <template slot-scope="scope">{{
        scope.row.postoffice ? $t("sim") : $t("nao")
      }}</template>
    </el-table-column>
    <el-table-column :label="`${$t('spread')}?`">
      <template slot-scope="scope">
        <select
          v-if="scope.row.type && parseInt(scope.row.type) === 2"
          name="customSpread"
          class="spread-select"
          v-model.number="scope.row.customSpread"
        >
          <option value=""></option>
          <option
            v-for="n in [20, 40, 60, 80, 100]"
            :key="n"
            :value="n"
            :selected="scope.row.customSpread === n"
          >
            {{ n }}%
          </option>
        </select>
        <span v-else>-</span>
      </template>
    </el-table-column>
    <el-table-column :label="`${$t('malha')}`">
      <template slot-scope="scope">
        <select
          v-if="scope.row.malha && isSelected(scope.row)"
          :key="`malha-${scope.row.id}-${selectedIds.join(',')}`"
          name="malhaDiscount"
          class="malha-select"
          :value="scope.row.malhaDiscount || 0"
          @change="(e) => { scope.row.malhaDiscount = parseFloat(e.target.value); }"
        >
          <option value="0"></option>
          <option
            v-for="n in malhaOptions(scope.row)"
            :key="n"
            :value="n"
          >
            {{ n }}%
          </option>
        </select>
        <span v-else>-</span>
      </template>
    </el-table-column>
  </el-table>
</template>
<script>
import { ServicesService } from "@/services/customers/services.services";

export default {
  name: "CustomerTableServices",
  props: {
    id: {
      type: [String, Number],
      required: false,
    },
  },
  data() {
    return {
      tableData: [],
      selectedList: [],
      selectedIds: [], // Array reativo com IDs dos serviços selecionados
    };
  },
  methods: {
    fetchRecords() {
      const vm = this;
      ServicesService.actives()
        .then((response) => {
          vm.tableData = response.records;
        })
        .catch((error) => {
          this.$message({
            type: "error",
            message: error,
            duration: 5000,
          });
        });
    },
    selected(list) {
      const vm = this;
      const customSpreadMap = new Map(
        list.map((item) => [item.id, item.customSpread])
      );
      // Mapeia customMalha (backend) para malhaDiscount (frontend)
      const malhaDiscountMap = new Map(
        list.map((item) => {
          let malhaValue = item.customMalha || item.malhaDiscount || 0;
          // Converte para número e garante que seja 0 se inválido
          malhaValue = parseFloat(malhaValue) || 0;
          return [item.id, malhaValue];
        })
      );
      
      // Atualiza a propriedade reativa selectedIds ANTES de selecionar
      vm.selectedIds = list.map(item => item.id);
      
      // Limpa seleção atual
      vm.$refs.tableServices.clearSelection();
      
      for (const record of list) {
        for (const row of vm.tableData) {
          if (row.id === record.id) {
            vm.$refs.tableServices.toggleRowSelection(row, true);
            row.customSpread = customSpreadMap.get(row.id) || null;
            row.malhaDiscount = parseFloat(malhaDiscountMap.get(row.id)) || 0;
          }
        }
      }
      
      // Force uma re-renderização em múltiplas etapas
      vm.$nextTick(() => {
        vm.$forceUpdate();
        
        // Timeout adicional para garantir sincronização completa
        setTimeout(() => {
          if (vm.$refs.tableServices && vm.$refs.tableServices.selection) {
            const currentSelection = vm.$refs.tableServices.selection.map(row => row.id);
            vm.selectedIds = currentSelection;
            vm.$forceUpdate();
          }
        }, 100);
        
        // Timeout extra para garantir
        setTimeout(() => {
          vm.$forceUpdate();
        }, 200);
      });
    },
    get() {
      // Retorna apenas os serviços selecionados com as propriedades necessárias para o backend
      return this.$refs.tableServices.selection.map(service => {
        // Garantir que customMalha seja sempre numérico
        let customMalhaValue = parseFloat(service.malhaDiscount) || 0;
        let customSpreadValue = parseFloat(service.customSpread) || 0;
        
        // Retorna apenas as propriedades que o backend precisa
        return {
          id: service.id,
          name: service.name,
          code: service.code,
          type: service.type,
          servicegroup: service.servicegroup,
          postoffice: service.postoffice,
          postofficeId: service.postofficeId,
          active: service.active,
          malha: service.malha,
          customMalha: customMalhaValue,
          customSpread: customSpreadValue
        };
      });
    },
    
    // Método para obter informações de customMalha de todos os serviços
    getAllServicesWithCustomData() {
      const vm = this;
      
      // Retorna TODOS os serviços com informações de customMalha/customSpread
      return vm.tableData.map(service => {
        const isSelected = vm.isSelected(service);
        
        // Se o serviço está selecionado, usa o valor atual
        // Se não está selecionado, sempre usa 0
        let customMalhaValue = 0;
        let customSpreadValue = 0;
        
        if (isSelected) {
          customMalhaValue = parseFloat(service.malhaDiscount) || 0;
          customSpreadValue = parseFloat(service.customSpread) || 0;
        }
        
        return {
          id: service.id,
          customMalha: customMalhaValue,
          customSpread: customSpreadValue
        };
      });
    },
    validate() {
      const vm = this;
      const group = {};
      for (const row of vm.$refs.tableServices.selection) {
        // Agrupa pelo CÓDIGO do grupo: grupos distintos podem exibir o mesmo nome
        const name = row.servicegroupCode || (row.servicegroup && row.servicegroup.code) || row.servicegroup || "Não definido";
        if (group[name]) {
          return false;
        }
        group[name] = true;
      }
      return false;
    },
    onSelectionChange(selection) {
      // Atualiza a lista reativa de IDs selecionados
      const newSelectedIds = selection.map(row => row.id);
      this.selectedIds = newSelectedIds;
      
      // Force update para garantir que os selects apareçam/sumam
      this.$forceUpdate();
    },
    isSelected(row) {
      // Múltiplas verificações para máxima confiabilidade
      const isInSelectedIds = this.selectedIds.includes(row.id);
      
      let isInTableSelection = false;
      if (this.$refs.tableServices && this.$refs.tableServices.selection) {
        isInTableSelection = this.$refs.tableServices.selection.some(selectedRow => selectedRow.id === row.id);
      }
      
      const result = isInSelectedIds || isInTableSelection;
      
      // Log especial para ECONOMICO 1 - PACKAGE
      if (row.name && row.name.includes('ECONOMICO 1')) {
        this.debugEconomico(row, isInSelectedIds, isInTableSelection, result);
      }
      

      
      return result;
    },
    debugEconomico(row, isInSelectedIds, isInTableSelection, result) {
      let tableSelectionData = [];
      if (this.$refs.tableServices && this.$refs.tableServices.selection) {
        tableSelectionData = this.$refs.tableServices.selection.map(s => ({id: s.id, name: s.name}));
      }
    },
    malhaOptions(row) {
      const base = [2.5, 5, 10, 15, 20];
      // +EXPRESSO (código 03220) é o único serviço que pode ter 40% de desconto de malha
      return String(row.code) === "03220" ? [...base, 40] : base;
    },
    filterHandler(value, row, column) {
      const property = column["property"];
      if (row[property]) {
        return row[property] === value;
      } else {
        return value === "Não definido";
      }
    },
  },
  computed: {
    filterGroup() {
      const vm = this;
      const list = [];
      for (const row of vm.tableData) {
        const group = row.servicegroup || "Não definido";
        if (!list.find((x) => x.value === group)) {
          list.push({
            text: group,
            value: group,
          });
        }
      }
      return list;
    },
  },
  mounted() {
    const vm = this;
    vm.fetchRecords();
    vm.selectedList = [];
  },
};
</script>
<style scoped>
.spread-select,
.malha-select {
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background-color: #f9f9f9;
  width: 70px;
}
.spread-select option,
.malha-select option {
  padding: 5px;
}
</style>
